import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Capacitor } from '@capacitor/core';

const STORAGE_KEY = 'bb-install-flyer-dismissed';
const InstallFlyerContext = createContext(null);

const isStandaloneDisplay = () => {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches
    || window.matchMedia('(display-mode: fullscreen)').matches
    || window.navigator.standalone === true
  );
};

const isNativeShell = () => {
  try {
    return Capacitor.isNativePlatform();
  } catch (error) {
    return false;
  }
};

const platformHints = () => {
  if (typeof navigator === 'undefined') {
    return { ios: false, android: false };
  }
  const ua = navigator.userAgent || '';
  const ios = /iphone|ipad|ipod/i.test(ua)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const android = /android/i.test(ua);
  return { ios, android };
};

const readQueuedPrompt = () => (
  typeof window !== 'undefined' ? window.__bbDeferredPrompt || null : null
);

export const useInstallFlyer = () => {
  const value = useContext(InstallFlyerContext);
  if (!value) {
    throw new Error('useInstallFlyer must be used within InstallFlyerProvider');
  }
  return value;
};

export const InstallFlyerProvider = ({ children }) => {
  const hidden = isNativeShell() || isStandaloneDisplay();
  const [open, setOpen] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(readQueuedPrompt);
  const [installed, setInstalled] = useState(hidden);

  useEffect(() => {
    if (hidden) return undefined;

    const takePrompt = (event) => {
      if (event && event.type === 'beforeinstallprompt') {
        event.preventDefault();
        window.__bbDeferredPrompt = event;
        setDeferredPrompt(event);
        return;
      }
      const queued = readQueuedPrompt();
      if (queued) setDeferredPrompt(queued);
    };

    const onInstalled = () => {
      window.__bbDeferredPrompt = null;
      setInstalled(true);
      setDeferredPrompt(null);
      setOpen(false);
      localStorage.setItem(STORAGE_KEY, '1');
    };

    window.addEventListener('beforeinstallprompt', takePrompt);
    window.addEventListener('bb-install-ready', takePrompt);
    window.addEventListener('appinstalled', onInstalled);
    takePrompt();

    if (!localStorage.getItem(STORAGE_KEY)) {
      setOpen(true);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', takePrompt);
      window.removeEventListener('bb-install-ready', takePrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, [hidden]);

  const dismiss = useCallback(() => {
    localStorage.setItem(STORAGE_KEY, '1');
    setOpen(false);
  }, []);

  const show = useCallback(() => {
    if (hidden || installed) return;
    setOpen(true);
  }, [hidden, installed]);

  const value = useMemo(() => ({
    show,
    canOffer: !hidden && !installed,
  }), [show, hidden, installed]);

  return (
    <InstallFlyerContext.Provider value={value}>
      {children}
      {open && !hidden && !installed && (
        <InstallFlyer
          deferredPrompt={deferredPrompt}
          onDismiss={dismiss}
          onPromptUsed={() => {
            window.__bbDeferredPrompt = null;
            setDeferredPrompt(null);
          }}
        />
      )}
    </InstallFlyerContext.Provider>
  );
};

const ShareGlyph = () => (
  <svg className="install-flyer-glyph" viewBox="0 0 24 24" aria-hidden="true">
    <path
      fill="currentColor"
      d="M12 3.2 7.6 7.5l1.2 1.2 2.4-2.3V15h1.6V6.4l2.4 2.3 1.2-1.2L12 3.2zM6 18.2V10H4.4v9.8h15.2V10H18v8.2H6z"
    />
  </svg>
);

const MenuGlyph = () => (
  <svg className="install-flyer-glyph" viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="12" cy="5.5" r="1.6" fill="currentColor" />
    <circle cx="12" cy="12" r="1.6" fill="currentColor" />
    <circle cx="12" cy="18.5" r="1.6" fill="currentColor" />
  </svg>
);

function InstallFlyer({ deferredPrompt, onDismiss, onPromptUsed }) {
  const { ios, android } = platformHints();
  const promptEvent = deferredPrompt || readQueuedPrompt();
  const canPrompt = Boolean(promptEvent);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onDismiss();
    };
    document.addEventListener('keydown', onKeyDown);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previous;
    };
  }, [onDismiss]);

  const handleInstall = async () => {
    const event = promptEvent || readQueuedPrompt();
    if (!event || typeof event.prompt !== 'function') return;
    event.prompt();
    const choice = await event.userChoice;
    onPromptUsed();
    if (choice.outcome === 'accepted') onDismiss();
  };

  return createPortal(
    <div className="install-flyer-overlay" onClick={onDismiss} role="presentation">
      <div
        className="install-flyer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="install-flyer-title"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className="install-flyer-close"
          onClick={onDismiss}
          aria-label="Close flyer"
        >
          ×
        </button>

        <p className="install-flyer-band">Band Bacon</p>

        <ul className="install-flyer-stamps">
          <li>Tonite</li>
          <li>All ages</li>
          <li>No cover</li>
        </ul>

        <h2 id="install-flyer-title" className="install-flyer-title">
          <span>Get this</span>
          <span>App</span>
        </h2>

        <p className="install-flyer-man">
          without going through
          <span>the man</span>
        </p>

        <ul className="install-flyer-banned">
          <li><s>App Store</s></li>
          <li><s>Google Play</s></li>
        </ul>

        {canPrompt && (
          <button
            type="button"
            className="install-flyer-stick"
            onClick={handleInstall}
          >
            Stick it on your phone
          </button>
        )}

        {ios && (
          <div className="install-flyer-iphone">
            <p className="install-flyer-iphone-label">On iPhone</p>
            <ol className="install-flyer-steps">
              <li>
                <span className="install-flyer-step-n">1</span>
                <span><ShareGlyph /> Tap <b>Share</b></span>
              </li>
              <li>
                <span className="install-flyer-step-n">2</span>
                <span>Add to Home Screen</span>
              </li>
            </ol>
          </div>
        )}

        {!ios && !canPrompt && (
          <p className="install-flyer-how">
            <MenuGlyph />
            {android
              ? <span>Browser menu → <b>Install app</b></span>
              : <span><b>Install</b> in the address bar</span>}
          </p>
        )}
      </div>
    </div>,
    document.body
  );
}

export default InstallFlyerProvider;
